// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useIdentify
 * @package @geenius/analytics/react
 * @description Wraps identity-management actions for the React variant so
 * consumers can identify or reset a user without re-plumbing tracker access.
 */

import type { UserTraits } from "@geenius/analytics";
import { useCallback } from "react";
import { useAnalytics } from "./useAnalytics";

/**
 * Identity-management helpers returned by {@link useIdentify}.
 *
 * @property identify Associates the current session with a user and traits.
 * @property reset Clears the current analytics identity and session state.
 */
export interface UseIdentifyReturn {
  identify: (userId: string, traits?: UserTraits) => Promise<void>;
  reset: () => Promise<void>;
}

/**
 * Returns identify and reset helpers backed by the active analytics tracker.
 *
 * @returns Stable identity-management functions for the active tracker.
 */
export function useIdentify(): UseIdentifyReturn {
  const { identify, reset } = useAnalytics();

  return {
    identify: useCallback(
      (userId: string, traits?: UserTraits) => identify(userId, traits),
      [identify],
    ),
    reset: useCallback(() => reset(), [reset]),
  };
}
